"use client";

import { MessageSquare, Check, CheckCheck, X, Clock } from "lucide-react";

/** Turn plain-text URLs into clickable links. App URLs (e.g. /schedule/) become relative paths to avoid ngrok interstitial. */
function Linkify({ text }: { text: string }) {
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const parts = text.split(urlRegex);
  return (
    <>
      {parts.map((part, i) => {
        if (!urlRegex.test(part)) return <span key={i}>{part}</span>;
        // Extract relative path for known app routes (avoids ngrok interstitial)
        let href = part;
        let displayText = part;
        try {
          const url = new URL(part);
          const appPaths = ["/schedule/", "/robo-advisor/"];
          if (appPaths.some((p) => url.pathname.startsWith(p))) {
            href = url.pathname + url.search + url.hash;
            displayText = href;
          }
        } catch { /* keep original href */ }
        return (
          <a
            key={i}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 underline hover:text-blue-800"
          >
            {displayText}
          </a>
        );
      })}
    </>
  );
}

interface MessageBlockProps {
  entryType: "sms_sent" | "sms_received" | "whatsapp_sent" | "whatsapp_received";
  body: string | null;
  status: string;
  createdAt: string;
  userName: string | null;
}

export function MessageBlock({ entryType, body, status, createdAt, userName }: MessageBlockProps) {
  const isWhatsApp = entryType.startsWith("whatsapp");
  const isSent = entryType.endsWith("_sent");
  const channelLabel = isWhatsApp ? "WhatsApp" : "SMS";

  const accentColor = isWhatsApp ? "border-green-500" : "border-teal-500";
  const containerClass = isSent
    ? `border-x-4 ${accentColor} bg-white`
    : `${isWhatsApp ? "bg-green-50" : "bg-teal-50"}`;

  const iconBg = isWhatsApp ? "bg-green-100 text-green-600" : "bg-teal-100 text-teal-600";

  const statusIcon: Record<string, { icon: typeof Check; color: string }> = {
    sent: { icon: Check, color: "text-gray-400" },
    delivered: { icon: CheckCheck, color: "text-green-500" },
    read: { icon: CheckCheck, color: "text-blue-500" },
    failed: { icon: X, color: "text-red-500" },
    pending: { icon: Clock, color: "text-yellow-500" },
    logged: { icon: Check, color: "text-gray-300" },
  };
  const { icon: StatusIcon, color: statusColor } = statusIcon[status] ?? statusIcon.logged;

  return (
    <div className={`flex ${isSent ? "justify-end" : "justify-start"}`}>
      <div className={`min-w-[85%] max-w-[85%] rounded-lg border border-gray-100 p-3 ${containerClass}`}>
        <div className="flex items-start gap-2">
          <div className={`rounded-full p-1.5 ${iconBg}`}>
            <MessageSquare className="h-3.5 w-3.5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between">
              <p className="text-xs font-medium text-gray-900">
                {channelLabel} {isSent ? "Sent" : "Received"}
              </p>
              <div className="flex flex-col items-end">
                <span className="text-[10px] text-gray-400">
                  {new Date(createdAt).toLocaleTimeString("en-ZA", { hour: "2-digit", minute: "2-digit" })}
                </span>
                <StatusIcon className={`h-3 w-3 ${statusColor}`} />
              </div>
            </div>
            {userName && isSent && <p className="text-[10px] text-gray-500">by {userName}</p>}
            {body && <p className="mt-1 text-xs text-gray-700 break-words whitespace-pre-wrap"><Linkify text={body} /></p>}
          </div>
        </div>
      </div>
    </div>
  );
}
